/**
 * Klasse, the second level (spec 10.1): one row per student with the derived numbers, sortable
 * by clicking a header, plus every action the teacher owns — create students, print the login
 * strips, export CSV, reset a PIN, delete a student, delete the class.
 *
 * PINs are hashed (spec 10.1), so the plain text exists only in the response that created or
 * reset them. It is handed to the strips screen through router state and never stored; leaving
 * that screen loses it, which is the intent.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL, authedJson, authedVoid } from "../shared/api.js";
import type { ClassDetail, ClassRow, CreatedStudent, ResetPin } from "../shared/api-types.js";
import { authedFetch } from "../shared/auth-store.js";
import { germanMessage } from "../shared/errors.js";
import { saveFile } from "../shared/platform.js";
import {
  ConfirmDialog,
  Empty,
  ErrorLine,
  Loading,
  Panel,
  TButton,
  formatDateTime,
  inputClass,
} from "./ui.js";
import type { StripData } from "./StripsScreen.js";

type SortKey = keyof Pick<
  ClassRow,
  "username" | "completed" | "learned" | "averageBest" | "today" | "lastActivity"
>;
type Direction = "asc" | "desc";

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: "username", label: "Benutzername", numeric: false },
  { key: "completed", label: "Themen abgeschlossen", numeric: true },
  { key: "learned", label: "Gelernt", numeric: true },
  { key: "averageBest", label: "Ø Punkte", numeric: true },
  { key: "today", label: "Heute", numeric: true },
  { key: "lastActivity", label: "Letzte Aktivität", numeric: false },
];

/** Spec 8.1: "25 Schüler anlegen" is the usual case, so that is the default. */
const DEFAULT_STUDENT_COUNT = 25;

export function ClassScreen() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sortKey, setSortKey] = useState<SortKey>("username");
  const [direction, setDirection] = useState<Direction>("asc");
  const [count, setCount] = useState(String(DEFAULT_STUDENT_COUNT));
  const [confirming, setConfirming] = useState<
    { kind: "student"; row: ClassRow } | { kind: "class" } | { kind: "reset"; row: ClassRow } | null
  >(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["teacher", "class", id],
    queryFn: () => authedJson<ClassDetail>("/teacher/classes/" + id, { subjectType: "teacher" }),
  });

  const klass = detail.data?.class;

  const createStudents = useMutation({
    mutationFn: (howMany: number) =>
      authedJson<CreatedStudent[]>("/teacher/classes/" + id + "/students", {
        method: "POST",
        body: JSON.stringify({ count: howMany }),
        subjectType: "teacher",
      }),
    onSuccess: (created) => {
      if (!klass) return;
      const state: StripData = {
        classNumber: klass.number,
        classLabel: klass.label,
        students: created.map((student) => ({ username: student.username, pin: student.pin })),
      };
      void queryClient.invalidateQueries({ queryKey: ["teacher", "class", id] });
      void queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      void navigate("/lehrer/klassen/" + id + "/streifen", { state });
    },
  });

  const resetPin = useMutation({
    mutationFn: (studentId: string) =>
      authedJson<ResetPin>("/teacher/students/" + studentId + "/reset-pin", {
        method: "POST",
        subjectType: "teacher",
      }),
    onSuccess: (result) => {
      if (!klass) return;
      setConfirming(null);
      const state: StripData = {
        classNumber: klass.number,
        classLabel: klass.label,
        students: [{ username: result.username, pin: result.pin }],
      };
      void navigate("/lehrer/klassen/" + id + "/streifen", { state });
    },
  });

  const deleteStudent = useMutation({
    mutationFn: (studentId: string) =>
      authedVoid("/teacher/students/" + studentId, {
        method: "DELETE",
        subjectType: "teacher",
      }),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ["teacher", "class", id] });
      await queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
    },
  });

  const deleteClass = useMutation({
    mutationFn: () =>
      authedVoid("/teacher/classes/" + id, { method: "DELETE", subjectType: "teacher" }),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ["teacher", "classes"] });
      void navigate("/lehrer", { replace: true });
    },
  });

  const rows = useMemo(
    () => sortRows(detail.data?.students ?? [], sortKey, direction),
    [detail.data, sortKey, direction],
  );

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setDirection((value) => (value === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("asc");
    }
  }

  /**
   * The CSV comes back with the bearer header like everything else, so it cannot be a plain
   * link; the body is turned into a file through the platform adapter (ADR 0005 bans
   * `window.open`).
   */
  async function exportCsv() {
    if (!klass) return;
    setExportError(null);
    try {
      const response = await authedFetch(API_URL, "/teacher/classes/" + id + "/export.csv", {
        subjectType: "teacher",
      });
      saveFile("klasse-" + klass.number + ".csv", await response.blob());
    } catch (err) {
      setExportError(germanMessage(err));
    }
  }

  if (detail.isPending) return <Loading />;
  if (detail.isError || !klass) {
    return <ErrorLine>{germanMessage(detail.error)}</ErrorLine>;
  }

  return (
    <>
      <nav className="mb-3 text-[14px]">
        <Link to="/lehrer" className="text-[var(--color-accent)] no-underline hover:underline">
          Klassen
        </Link>
        <span className="text-[var(--color-muted)]"> / {klass.label}</span>
      </nav>
      <div className="mb-5 flex flex-wrap items-baseline gap-3">
        <h1 className="m-0 text-[26px] font-bold">Klasse {klass.label}</h1>
        <span className="text-[15px] text-[var(--color-muted)]">
          Klassennummer {klass.number} · {rows.length}{" "}
          {rows.length === 1 ? "Schüler" : "Schülerinnen und Schüler"}
        </span>
      </div>

      <Panel
        title="Schülerinnen und Schüler"
        actions={
          <>
            <label className="flex items-center gap-2 text-[14px]">
              <span className="text-[var(--color-muted)]">Anzahl</span>
              <input
                type="number"
                min={1}
                max={40}
                value={count}
                onChange={(event) => setCount(event.target.value)}
                aria-label="Anzahl neuer Schüler"
                className={inputClass + " w-20"}
              />
            </label>
            <TButton
              variant="primary"
              disabled={createStudents.isPending}
              onClick={() => {
                const howMany = Number(count);
                if (Number.isInteger(howMany) && howMany >= 1 && howMany <= 40) {
                  createStudents.mutate(howMany);
                }
              }}
            >
              {createStudents.isPending ? "…" : "Schüler anlegen"}
            </TButton>
            <TButton onClick={() => void exportCsv()}>CSV exportieren</TButton>
            <TButton variant="danger" onClick={() => setConfirming({ kind: "class" })}>
              Klasse löschen
            </TButton>
          </>
        }
      >
        {(createStudents.isError || exportError) && (
          <div className="px-4 pt-3">
            <ErrorLine>{exportError ?? germanMessage(createStudents.error)}</ErrorLine>
          </div>
        )}

        {rows.length === 0 ? (
          <Empty>
            Noch keine Schülerinnen und Schüler. Leg mit «Schüler anlegen» welche an — du bekommst
            die Anmeldestreifen zum Ausdrucken.
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="t-table">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={
                        sortKey === column.key
                          ? direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <TButton
                        variant="quiet"
                        onClick={() => toggleSort(column.key)}
                        className="!text-[13px] !font-bold text-[var(--color-muted)]"
                      >
                        {column.label}
                        {sortKey === column.key ? (direction === "asc" ? " ▲" : " ▼") : ""}
                      </TButton>
                    </th>
                  ))}
                  <th scope="col">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link
                        to={"/lehrer/schueler/" + row.id}
                        className="font-semibold text-[var(--color-accent)] no-underline hover:underline"
                      >
                        {row.username}
                      </Link>
                    </td>
                    <td className="t-num">{row.completed}</td>
                    <td className="t-num">{row.learned}</td>
                    <td className="t-num">
                      {row.averageBest === null
                        ? "—"
                        : row.averageBest.toFixed(1).replace(".", ",")}
                    </td>
                    <td className="t-num">{row.today} von 3</td>
                    <td>{formatDateTime(row.lastActivity)}</td>
                    <td>
                      <div className="flex justify-end gap-2 whitespace-nowrap">
                        <TButton
                          variant="quiet"
                          onClick={() => setConfirming({ kind: "reset", row })}
                        >
                          PIN zurücksetzen
                        </TButton>
                        <TButton
                          variant="quiet"
                          className="!text-[var(--color-danger)]"
                          onClick={() => setConfirming({ kind: "student", row })}
                        >
                          Löschen
                        </TButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {confirming?.kind === "reset" && (
        <ConfirmDialog
          title="PIN zurücksetzen"
          body={
            <>
              {confirming.row.username} bekommt einen neuen PIN. Der alte gilt sofort nicht mehr,
              und wer noch angemeldet ist, wird abgemeldet. Du siehst den neuen PIN genau einmal,
              auf dem Anmeldestreifen danach.
            </>
          }
          confirmLabel="PIN zurücksetzen"
          pending={resetPin.isPending}
          error={resetPin.isError ? germanMessage(resetPin.error) : null}
          onConfirm={() => resetPin.mutate(confirming.row.id)}
          onClose={() => {
            setConfirming(null);
            resetPin.reset();
          }}
        />
      )}

      {confirming?.kind === "student" && (
        <ConfirmDialog
          title="Schüler löschen"
          body={
            <>
              {confirming.row.username} wird gelöscht, mit allen Ergebnissen. Das lässt sich nicht
              rückgängig machen.
            </>
          }
          confirmLabel="Löschen"
          pending={deleteStudent.isPending}
          error={deleteStudent.isError ? germanMessage(deleteStudent.error) : null}
          onConfirm={() => deleteStudent.mutate(confirming.row.id)}
          onClose={() => {
            setConfirming(null);
            deleteStudent.reset();
          }}
        />
      )}

      {confirming?.kind === "class" && (
        <ConfirmDialog
          title="Klasse löschen"
          body={
            <>
              Die Klasse {klass.label} wird gelöscht, mit allen {rows.length} Schülerinnen und
              Schülern und deren Ergebnissen. Das lässt sich nicht rückgängig machen.
            </>
          }
          confirmLabel="Klasse löschen"
          confirmText={klass.label}
          pending={deleteClass.isPending}
          error={deleteClass.isError ? germanMessage(deleteClass.error) : null}
          onConfirm={() => deleteClass.mutate()}
          onClose={() => {
            setConfirming(null);
            deleteClass.reset();
          }}
        />
      )}
    </>
  );
}

/** Nulls last whichever way the column is sorted: "no activity yet" is not a small date. */
export function sortRows(rows: ClassRow[], key: SortKey, direction: Direction): ClassRow[] {
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = a[key];
    const right = b[key];
    if (left === null && right === null) return a.number - b.number;
    if (left === null) return 1;
    if (right === null) return -1;
    if (typeof left === "number" && typeof right === "number") return (left - right) * factor;
    return String(left).localeCompare(String(right), "de-CH", { numeric: true }) * factor;
  });
}
